/**
 * ProfessionalDetailPage
 * Detail view for a single professional
 */

import { Card, CardContent, CardHeader } from '@/shared/components/ui/card'
import { ProfessionalContactButtons } from '@features/professionals/components/ProfessionalContactButtons'
import { ProfessionalHeader } from '@features/professionals/components/ProfessionalHeader'
import { ProfessionalInfoCards } from '@features/professionals/components/ProfessionalInfoCards'
import { ProfessionalReviews } from '@features/professionals/components/ProfessionalReviews'
import { UnclaimedBanner } from '@features/professionals/components/UnclaimedBanner'
import { useProfessional } from '@features/professionals/hooks/useProfessionals'
import { Button } from '@shared/components/ui/button'
import { usePendingAction } from '@shared/hooks'
import { Home, Loader2, RefreshCw, Search } from 'lucide-react'
import { useNavigate, useParams } from 'react-router-dom'

// ============================================================================
// Component
// ============================================================================

export function ProfessionalDetailPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const { data: professional, isLoading, error } = useProfessional(id || '')

  // Execute pending actions when returning to this page
  usePendingAction()

  // Loading state
  if (isLoading) {
    return (
      <>
        <title>ProFessionals - Cargando...</title>
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="text-center">
            <Loader2 className="h-12 w-12 animate-spin mx-auto mb-4 text-primary" />
            <p className="text-muted-foreground">Cargando perfil del profesional...</p>
          </div>
        </div>
      </>
    )
  }

  // Error state — do NOT redirect to /login from a public route
  if (error) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="text-center">
          <h2 className="text-2xl font-bold mb-4">No pudimos cargar este profesional</h2>
          <p className="text-muted-foreground mb-6">
            El perfil no está disponible en este momento. Intentá de nuevo o buscá otro profesional.
          </p>
          <div className="flex gap-3 justify-center">
            <Button onClick={() => window.location.reload()}>
              <RefreshCw className="h-4 w-4 mr-2" />
              Reintentar
            </Button>
            <Button variant="outline" onClick={() => void navigate('/')}>
              <Search className="h-4 w-4 mr-2" />
              Buscar profesionales
            </Button>
            <Button variant="ghost" onClick={() => void navigate('/')}>
              <Home className="h-4 w-4 mr-2" />
              Inicio
            </Button>
          </div>
        </div>
      </div>
    )
  }

  // Not found state
  if (!professional) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="text-center">
          <h2 className="text-2xl font-bold mb-4">Profesional no encontrado</h2>
          <p className="text-muted-foreground mb-6">
            El profesional que buscas no existe o ha sido eliminado.
          </p>
          <Button onClick={() => void navigate('/')}>
            <Home className="h-4 w-4 mr-2" />
            Ir al inicio
          </Button>
        </div>
      </div>
    )
  }

  // Prepare metadata
  const professionName = professional.profession?.name || 'Profesional'
  const profileUrl = `${window.location.origin}/professional/${professional.id}`
  const description = professional.bio
    ? `${professional.professionalName} - ${professionName}. ${professional.bio.substring(0, 150)}`
    : `${professional.professionalName} - ${professionName}. Profesional en ProFessionals con ${professional.averageRating.toFixed(1)} estrellas.`
  const ogImage = 'https://tests.professionals.com.ar/favicon.png'

  return (
    <>
      {/* React 19 Native Metadata Tags */}
      <title>{professional.professionalName} - {professionName} | ProFessionals</title>
      <meta name="description" content={description} />

      {/* Open Graph Meta Tags for Social Sharing */}
      <meta property="og:type" content="profile" />
      <meta property="og:title" content={`${professional.professionalName} - ${professionName}`} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:image:width" content="512" />
      <meta property="og:image:height" content="512" />
      <meta property="og:url" content={profileUrl} />

      {/* Twitter Card Meta Tags */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={`${professional.professionalName} - ${professionName}`} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={ogImage} />
      <meta name="twitter:image:width" content="512" />
      <meta name="twitter:image:height" content="512" />

      <div className="container mx-auto py-8 pt-2 min-h-screen bg-background">

        {/* Header */}

        {/* Contact Buttons */}
        <div className="container mx-auto px-4 py-2">
          <Card>
            <CardHeader className='pt-2 pb-1' data-tour="professional-header">
              <ProfessionalHeader professional={professional} />
            </CardHeader>
            <CardContent className='pb-2' data-tour="professional-contact">
              <ProfessionalContactButtons professional={professional} />
            </CardContent>
          </Card>
        </div>

        {/* Main Content */}
        <div className="container mx-auto px-4 py-2">

          {/* About Section */}
          {/* <ProfessionalAbout professional={professional} /> */}

          {/* Info Cards */}
          <div data-tour="professional-info">
            <ProfessionalInfoCards professional={professional} />
          </div>

          {/* Reviews Section - or Unclaimed Banner for unverified profiles */}
          <div data-tour="professional-reviews">
            {professional.status === 'UNCLAIMED' ? (
              <UnclaimedBanner professionalName={professional.professionalName} />
            ) : (
              <ProfessionalReviews professional={professional} />
            )}
          </div>
        </div>
      </div>
    </>
  )
}
